import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Big } from 'big.js';
import { isArray, isDefined } from 'ionic-angular/util/util';
import { BaseController } from '../../../../core/controllers/base.controller';
import { SelectOption } from '../../../../core/interfaces/select-option';
import { VasAppFacade } from '../../../services/vas-app-facade.service';
import { CostsControlConfig } from '../costs-control-config.model';
import { CostsControl } from '../costs-control.model';
import { CostsControlItem } from './costs-control-form-value.model';
import { CostsControlSettings } from './costs-control.settings';

@Injectable()
export class CostsControlController extends BaseController {

    protected _form: FormArray = new FormArray([]);
    protected control: CostsControl;
    protected config: CostsControlConfig;
    protected _costOptions: Array<SelectOption> = [];
    currencyFormControl: FormControl = new FormControl();

    constructor(protected appFacade: VasAppFacade) {
        super();
    }

    initialise(control: CostsControl): void {

        this.control = control;
        this.config = control.config;

        this.appFacade.objects.lookup.store
            .getAll()
            .map(lookups => lookups.filter(lookup => lookup.lookupType === this.config.lookupType))
            .map(lookups => lookups.map(lookup => new SelectOption(lookup.id, lookup.title)))
            .take(1)
            .subscribe(selectOptions => this._costOptions = selectOptions);

        this.empty();

        this.currencyFormControl.valueChanges
            .takeUntil(this.ngUnsubscribe)
            .subscribe(() => this.currencyControlToRows());
    }

    get costOptions(): Array<SelectOption> {
        return this._costOptions;
    }

    get currency(): string {
        return this.currencyFormControl.value || this.config.currency;
    }

    get currencyLabel(): string {
        return CostsControlSettings.currencyOptions.find(item => item.value === this.currency).label;
    }

    get currencyOptions(): Array<SelectOption> {
        return CostsControlSettings.currencyOptions
            .map(item => new SelectOption(item.value, item.label));
    }

    get form(): FormArray {
        return this._form;
    }

    get netTotal(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => item.agreed || 0)
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get showLabels(): boolean {
        return this._form.length > 0;
    }

    get total(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.agreed || 0).mul(new Big(item.vatRate !== 'n/a' ? item.vatRate : 0).plus(1)))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get totalSavings(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.estimated || 0).sub(new Big(item.agreed || 0)).mul(new Big(item.vatRate !== 'n/a' ? item.vatRate : 0).plus(1)))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get totalEstimated(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.estimated || 0).mul(new Big(item.vatRate !== 'n/a' ? item.vatRate : 0).plus(1)))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get totalNetSavings(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.estimated || 0).sub(new Big(item.agreed || 0)))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get totalNetEstimated(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.estimated || 0))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get vatDefault(): string | number {
        return this.config.vatRate;
    }

    get vatOptions(): Array<SelectOption> {
        return CostsControlSettings.vatOptions
            .map(item => new SelectOption(item.value, item.label));
    }

    get vatTotal(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.agreed || 0).mul(new Big(item.vatRate !== 'n/a' ? item.vatRate : 0)))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get savingsVatTotal(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.estimated || 0).sub(new Big(item.agreed || 0)).mul(new Big(item.vatRate !== 'n/a' ? item.vatRate : 0)))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    get estimatedVatTotal(): string {

        const rows: Array<CostsControlItem> = this._form.getRawValue();

        try {
            return rows
                .map(item => new Big(item.estimated || 0).mul(new Big(item.vatRate !== 'n/a' ? item.vatRate : 0)))
                .reduce((prev, curr) => prev.plus(curr), new Big(0))
                .toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    initialiseDefaultValue(): void {

        if (!isDefined(this.control.defaultValue) || !isArray(this.control.defaultValue)) {
            return;
        }

        this.writeValue(this.control.defaultValue as Array<CostsControlItem>);
    }

    writeValue(items: Array<CostsControlItem> = []): void {
        this.empty();
        (isArray(items) ? items : []).forEach(item => this.addItem(item));
        this.rowsToCurrencyControl();
    }

    addItem(item: CostsControlItem): void {
        const fg = new FormGroup({
            estimated: new FormControl(item.estimated || 0),
            agreed: new FormControl(item.agreed || 0),
            vatRate: new FormControl(isDefined(item.vatRate) ? item.vatRate : this.config.vatRate),
            lookup: new FormControl(item.lookup || 0),
            currency: new FormControl(item.currency || this.currency)
        });

        this._form.push(fg);
    }

    remove(i: number): void {
        this._form.removeAt(i);
    }

    reset(): void {
        this.initialiseDefaultValue();
    }

    empty(): void {

        while (this._form.length) {
            this._form.removeAt(0);
        }
    }

    setValue(values: Array<CostsControlItem>): void {
        this.empty();

        (values || []).forEach(value => {
            this.form.push(new FormControl(value));
        });
    }

    getSaving(estimated: number, agreed: number): string {
        try {
            return new Big(estimated).sub(agreed).toFixed(2);
        } catch (e) {
            return '0.00';
        }
    }

    rowsToCurrencyControl(): void {
        // either the currency value of the first item, or the default currency from the control configuration
        this.currencyFormControl.setValue(
            this.form.length ? this.form.at(0).value.currency : this.config.currency || 'GBP'
        );
    }

    currencyControlToRows(): void {

        const currency = this.currencyFormControl.value;

        this.form.controls.forEach(control => {
            control.patchValue({currency});
        });
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/costs/form/costs-control.controller.ts